export type CreatorModuleOverrideMode = 'grant' | 'deny' | null;

export type CreatorModuleAvailabilityInput = {
  platformEnabled: boolean;
  envKillSwitchActive: boolean;
  creatorEligible: boolean;
  overrideMode: CreatorModuleOverrideMode;
};

export type CreatorModuleAvailability = {
  enabled: boolean;
  source:
    | 'env_kill_switch'
    | 'creator_ineligible'
    | 'creator_deny'
    | 'creator_grant'
    | 'platform';
};

/**
 * Resolve whether an eligible creator may operate a Studio module.
 * Creator grants can lift a registry/Admin platform-off state, but never an
 * environment kill switch or missing Creator Plan entitlement.
 */
export function resolveCreatorModuleAvailability(
  input: CreatorModuleAvailabilityInput,
): CreatorModuleAvailability {
  if (input.envKillSwitchActive) {
    return { enabled: false, source: 'env_kill_switch' };
  }
  if (!input.creatorEligible) {
    return { enabled: false, source: 'creator_ineligible' };
  }
  if (input.overrideMode === 'deny') {
    return { enabled: false, source: 'creator_deny' };
  }
  if (input.overrideMode === 'grant') {
    return { enabled: true, source: 'creator_grant' };
  }
  return { enabled: input.platformEnabled, source: 'platform' };
}

export enum CreatorModuleAction {
  MANAGE_READ = 'manage_read',
  MANAGE_WRITE = 'manage_write',
  PUBLIC_DISCOVERY = 'public_discovery',
  NEW_ACQUISITION = 'new_acquisition',
  EXISTING_CUSTOMER_ACCESS = 'existing_customer_access',
  OBLIGATION_OPERATION = 'obligation_operation',
}

/**
 * Module suspension blocks new business while preserving existing customer
 * access and operational obligations. Ownership/entitlement checks remain
 * mandatory in the owning API service.
 */
export function isCreatorModuleActionAllowed(
  action: CreatorModuleAction,
  moduleEnabled: boolean,
): boolean {
  if (
    action === CreatorModuleAction.EXISTING_CUSTOMER_ACCESS ||
    action === CreatorModuleAction.OBLIGATION_OPERATION
  ) {
    return true;
  }
  return moduleEnabled;
}
